// Wave 20.49ae — Web parity for the mobile Drops surface.
// Three scope tabs (for_you / following / mine) each backed by
// a different /drops/* endpoint. Mine has a "Show expired"
// toggle (default off) so a rider drowning in month-old drops
// can still find the fresh ones.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { colors } from '../theme';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

type Scope = 'for_you' | 'following' | 'mine';

interface InboxItem {
  id: string;
  dropType: 'activity' | 'peak';
  dropToken: string;
  url: string;
  sender: { id: number; username: string | null; displayName: string };
  seenAt: string | null;
  createdAt: string;
  preview: { title: string; subtitle: string };
  expired: boolean;
}

interface SentItem {
  id: string;
  dropType: 'activity' | 'peak';
  dropToken: string;
  url: string;
  recipient: {
    id: number;
    username: string | null;
    displayName: string;
  } | null;
  createdAt: string;
  expiresAt: string | null;
  expired: boolean;
  preview: { title: string; subtitle: string };
}

interface FollowingItem {
  id: string;
  dropType: 'activity' | 'peak';
  dropToken: string;
  url: string;
  author: { id: number; username: string | null; displayName: string };
  createdAt: string;
  expiresAt: string | null;
  expired: boolean;
  preview: { title: string; subtitle: string };
}

function fmtExpiresIn(iso: string | null, expired: boolean): string {
  if (expired) return 'EXPIRED';
  if (!iso) return 'NO EXPIRATION';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'EXPIRED';
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m LEFT`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h LEFT`;
  return `${Math.floor(h / 24)}d LEFT`;
}

async function fetchJson<T>(path: string): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}

export default function DropsSurface() {
  const [scope, setScope] = useState<Scope>('for_you');
  const [showExpired, setShowExpired] = useState(false);
  return (
    <Box>
      <Tabs
        value={scope}
        onChange={(_, v) => setScope(v as Scope)}
        variant="fullWidth"
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            color: colors.textMuted,
            fontWeight: 700,
            textTransform: 'none',
            minHeight: 40,
          },
          '& .Mui-selected': { color: `${colors.brand} !important` },
          '& .MuiTabs-indicator': { backgroundColor: colors.brand },
        }}
      >
        <Tab value="for_you" label="For you" />
        <Tab value="following" label="Following" />
        <Tab value="mine" label="Mine" />
      </Tabs>
      {scope === 'mine' && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showExpired}
                onChange={(e) => setShowExpired(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.brand },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: `${colors.brand} !important`,
                    opacity: 0.6,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                Show expired
              </Typography>
            }
          />
        </Box>
      )}
      {scope === 'for_you' && <InboxList />}
      {scope === 'following' && <FollowingList />}
      {scope === 'mine' && <SentList hideExpired={!showExpired} />}
    </Box>
  );
}

function InboxList() {
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setItems(await fetchJson<InboxItem[]>('/drops/inbox'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  if (error) return <ErrorText>{error}</ErrorText>;
  if (items === null) return <LoadingSpinner />;
  if (items.length === 0) {
    return <EmptyText>No drops yet — nothing has been sent to you.</EmptyText>;
  }
  return (
    <Stack spacing={1}>
      {items.map((it) => (
        <DropRow
          key={it.id}
          who={`From ${it.sender.displayName}`}
          username={it.sender.username}
          title={it.preview.title}
          subtitle={it.preview.subtitle}
          url={it.url}
          expiresAt={null}
          expired={it.expired}
        />
      ))}
    </Stack>
  );
}

function FollowingList() {
  const [items, setItems] = useState<FollowingItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setItems(await fetchJson<FollowingItem[]>('/drops/following'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  if (error) return <ErrorText>{error}</ErrorText>;
  if (items === null) return <LoadingSpinner />;
  const visible = items.filter((it) => !it.expired);
  if (visible.length === 0) {
    return (
      <EmptyText>
        No public drops from people you follow — follow more riders or check
        back after they share.
      </EmptyText>
    );
  }
  return (
    <Stack spacing={1}>
      {visible.map((it) => (
        <DropRow
          key={it.id}
          who={`From ${it.author.displayName}`}
          username={it.author.username}
          title={it.preview.title}
          subtitle={it.preview.subtitle}
          url={it.url}
          expiresAt={it.expiresAt}
          expired={it.expired}
        />
      ))}
    </Stack>
  );
}

function SentList({ hideExpired }: { hideExpired: boolean }) {
  const [items, setItems] = useState<SentItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setItems(await fetchJson<SentItem[]>('/drops/sent'));
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  if (error) return <ErrorText>{error}</ErrorText>;
  if (items === null) return <LoadingSpinner />;
  const visible = hideExpired ? items.filter((it) => !it.expired) : items;
  if (visible.length === 0) {
    return (
      <EmptyText>
        {hideExpired && items.length > 0
          ? 'No active drops — flip "Show expired" to see older ones.'
          : 'No drops sent yet — share a peak or activity to see it here.'}
      </EmptyText>
    );
  }
  return (
    <Stack spacing={1}>
      {visible.map((it) => (
        <DropRow
          key={it.id}
          who={it.recipient ? `To ${it.recipient.displayName}` : 'Public link'}
          username={it.recipient?.username ?? null}
          title={it.preview.title}
          subtitle={it.preview.subtitle}
          url={it.url}
          expiresAt={it.expiresAt}
          expired={it.expired}
        />
      ))}
    </Stack>
  );
}

function DropRow({
  who,
  username,
  title,
  subtitle,
  url,
  expiresAt,
  expired,
}: {
  who: string;
  username: string | null;
  title: string;
  subtitle: string;
  url: string;
  expiresAt: string | null;
  expired: boolean;
}) {
  const chip = fmtExpiresIn(expiresAt, expired);
  const soon =
    expiresAt && new Date(expiresAt).getTime() - Date.now() < 3_600_000;
  const chipColor = expired
    ? colors.textFaint
    : soon
    ? colors.warning
    : colors.brand;
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.surface,
        opacity: expired ? 0.55 : 1,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': { borderColor: colors.brand },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography sx={{ color: colors.text, fontWeight: 700, fontSize: '0.85rem' }}>
            {who}
          </Typography>
          {username && (
            <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
              @{username}
            </Typography>
          )}
        </Stack>
        <Typography sx={{ color: colors.text, fontWeight: 600, mt: 0.25 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Chip
        label={chip}
        size="small"
        sx={{
          fontWeight: 800,
          fontSize: '0.65rem',
          letterSpacing: '0.05em',
          color: chipColor,
          borderColor: chipColor,
          backgroundColor: 'transparent',
          border: `1px solid ${chipColor}`,
        }}
      />
    </Box>
  );
}

function LoadingSpinner() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress size={24} sx={{ color: colors.brand }} />
    </Box>
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ color: colors.danger, textAlign: 'center', py: 2 }}>
      {children}
    </Typography>
  );
}

function EmptyText({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ color: colors.textMuted, textAlign: 'center', py: 3 }}>
      {children}
    </Typography>
  );
}
